import React, { useCallback, useEffect, useState } from "react";
import MUIDataTable from "mui-datatables";
import { Button } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Axios from "../axios";

const formatAddress = (address = {}) =>
  [address.line1, address.city, address.state, address.postal_code, address.country]
    .filter(Boolean)
    .join(", ") || "-";

const RacePurchases = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND;

  const [rows, setRows] = useState([]);
  const [raceName, setRaceName] = useState("");
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const showError = (err) => {
    toast.error(err?.response?.data?.message || err?.response?.data?.error || "Something went wrong");
  };

  const fetchPurchases = useCallback(
    (pageIndex, searchText) => {
      setLoading(true);
      Axios.get(`${BACKEND_URL}race/${id}/purchases`, {
        params: {
          page: pageIndex + 1,
          limit: 10,
          search: searchText,
        },
      })
        .then((res) => {
          setRows(res?.data?.data?.docs || []);
          setCount(res?.data?.data?.totalDocs || 0);
          setRaceName(res?.data?.data?.raceName || "");
        })
        .catch(showError)
        .finally(() => setLoading(false));
    },
    [BACKEND_URL, id]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPurchases(0, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPurchases]);

  const columns = [
    { name: "itemName", label: "Item" },
    { name: "qty", label: "Quantity" },
    {
      name: "price",
      label: "Price",
      options: {
        customBodyRenderLite: (i) => `$${Number(rows[i]?.price || 0).toFixed(2)}`,
      },
    },
    {
      name: "customerName",
      label: "Customer Name",
      options: {
        sort: false,
        customBodyRenderLite: (i) => rows[i]?.customer?.fullName || "-",
      },
    },
    {
      name: "customerEmail",
      label: "Customer Email",
      options: {
        sort: false,
        customBodyRenderLite: (i) => rows[i]?.customer?.email || "-",
      },
    },
    {
      name: "customerPhone",
      label: "Customer Phone",
      options: {
        sort: false,
        customBodyRenderLite: (i) => rows[i]?.customer?.phone || "-",
      },
    },
    {
      name: "address",
      label: "Shipping / Billing Address",
      options: {
        sort: false,
        customBodyRenderLite: (i) => formatAddress(rows[i]?.customer?.address),
      },
    },
    {
      name: "createdAt",
      label: "Order Date",
      options: {
        customBodyRenderLite: (i) => (rows[i]?.createdAt ? new Date(rows[i].createdAt).toLocaleDateString() : "-"),
      },
    },
  ];

  return (
    <div className="container-fluid">
      <Helmet>
        <title>Race Cation Purchases</title>
      </Helmet>
      <div className="d-flex justify-content-between align-center mb-3">
        <h2>Purchases{raceName ? ` — ${raceName}` : ""}</h2>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate("/race-management")}>
          Back to Race Cations
        </Button>
      </div>

      <MUIDataTable
        title="Purchases list"
        data={rows}
        columns={columns}
        options={{
          responsive: "standard",
          selectableRows: "none",
          filter: false,
          search: true,
          viewColumns: false,
          sort: false,
          serverSide: true,
          count,
          page,
          rowsPerPageOptions: [],
          download: false,
          textLabels: {
            body: {
              noMatch: loading ? "Loading..." : "No purchases yet for this race cation.",
            },
          },
          onTableChange: (action, tableState) => {
            if (action === "changePage") {
              setPage(tableState.page);
              fetchPurchases(tableState.page, search);
            }
          },
          onSearchChange: (searchText) => {
            const searchValue = searchText || "";
            setSearch(searchValue);
            setPage(0);
            fetchPurchases(0, searchValue);
          },
        }}
      />
    </div>
  );
};

export default RacePurchases;
